/**
 * M4.V4 — the calendar board view (`specs/M4-V4-calendar.md`).
 *
 * One `BoardDataSource.load()` with the view's filter and sort, paged to exhaustion under a
 * 2,000-item ceiling (decision D-V4-1), then bucketed by day in the browser. The colour axis
 * is `bucketsOf` — the same member the quick-filter facets and group-by use — so a pill and a
 * grid cell can never disagree about what colour a status is.
 *
 * The grid is CSS grid, not react-big-calendar: monday's is rbc (measured, `[V4§2]`) but the
 * month view is 60 lines and a dependency would buy nothing.
 *
 * Dragging a pill to another day is **dnd-kit** (`shared/dnd.ts`, round 5x): a pill is a
 * `useDraggable`, a day cell a `useDroppable`, and `KeyboardSensor` moves a pill with the
 * arrow keys — the keyboard alternative the HTML5 drag API this started on could not offer
 * (q405/q414).
 */
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  calendarDateColumnIds,
  calendarEvents,
  CALENDAR_METRICS,
  eventsByDay,
  eventsPerDayCell,
  findColumnType,
  shiftCellValue,
  type CalendarEvent,
  type CalendarRange,
  type CalendarViewSettings,
  type CellBucket,
} from '@elizmonday/shared';
import { EMPTY_STATUS_COLOR, Icon, icons, PageEmptyState, PageSkeleton, statusColorHex } from '@elizmonday/ui';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { BoardColumn, BoardDataSource, ItemRow } from '../../board-view/data/types';
import { dateRangeFilter } from '../shared/date-range';
import { useViewDragSensors } from '../shared/dnd';
import { useViewData } from '../shared/use-view-data';
import { useViewSettings, type ViewSettingsOverride } from '../shared/use-view-settings';
import { ViewToolbar } from '../shared/ViewToolbar';
import { BoardViewToolbar, useViewQueryControls } from '../shared/BoardViewToolbar';
import { calendarGrid, rangeTitle, shiftAnchor, todayString, weekdayLabels } from './grid';
import './calendar.css';

/** D-V4-1: a calendar places every matching item, and says so when it stops. */
const ITEM_CEILING = 2000;
/**
 * monday's blank bucket, drawn in the palette's own grey — the same colour `board-read.ts`
 * gives a virtual group with no colour of its own, and the legend calls it *Empty* (measured).
 */
const BLANK_COLOR = statusColorHex(EMPTY_STATUS_COLOR);
const EMPTY_BUCKET: CellBucket = { key: '$$$blank$$$', label: 'Empty', color: BLANK_COLOR };

export function CalendarView({
  boardId,
  viewId,
  source,
  settingsOverride,
}: {
  boardId: string;
  viewId: string;
  source: BoardDataSource;
  /** The `/__ui/views` harness supplies its own settings; the product route does not. */
  settingsOverride?: ViewSettingsOverride<CalendarViewSettings>;
}) {
  const handle = useViewSettings<CalendarViewSettings>(boardId, viewId, 'calendar', settingsOverride);
  const settings = handle.settings;
  const navigate = useNavigate();
  const today = todayString();
  const [anchor, setAnchor] = useState(today);
  const [range, setRange] = useState<CalendarRange>(settings.range);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The read runs **twice**: a probe with no date predicate, only to learn the board's
   * columns (which date columns the settings name is a question about the board, and the
   * grid's window is a question about the anchor), then the real read narrowed to the visible
   * days. Both go through the same query cache key, so the probe is one round trip on mount.
   */
  const probe = useViewData({ source, boardId, viewId });
  const columns = probe.payload?.columns ?? [];

  const dateColumnIds = useMemo(
    () => calendarDateColumnIds(settings, columns.map((column) => ({ id: column.id, type: column.type }))),
    [settings, columns],
  );
  const colorColumn = useMemo(
    () => resolveColorColumn(columns, settings.colorColumnId),
    [columns, settings.colorColumnId],
  );

  const grid = useMemo(
    () => calendarGrid({ anchor, range, weekStartsOn: settings.weekStartsOn, today }),
    [anchor, range, settings.weekStartsOn, today],
  );

  /**
   * D-V4-1, closed: the days on screen are a filter on the placing columns, compiled to SQL by
   * the service — `between` for a `date`, and since round 5z the two-ended overlap for a
   * `timeline` (`shared/date-range.ts`). When it cannot be expressed the read is unnarrowed
   * and the 2,000-item ceiling below still applies.
   */
  const rangeFilter = useMemo(
    () => dateRangeFilter({ columns, dateColumnIds, rangeStart: grid.start, rangeEnd: grid.end }),
    [columns, dateColumnIds, grid.start, grid.end],
  );

  /**
   * P-025: the board toolbar. Its filter is AND-ed with the calendar's own range narrowing —
   * the range is a read optimisation, not a user filter, so neither may drop the other.
   */
  const toolbar = useViewQueryControls();
  const toolbarFilter = toolbar.state.filter ?? null;

  const data = useViewData({
    source,
    boardId,
    viewId,
    state: {
      ...toolbar.state,
      ...(rangeFilter === null
        ? {}
        : {
            filter:
              toolbarFilter === null
                ? rangeFilter
                : { operator: 'and' as const, rules: [], groups: [rangeFilter, toolbarFilter] },
          }),
    },
  });
  const rows = data.allRows();

  // Whatever the range filter could not narrow is still paged to exhaustion under the ceiling.
  const truncated = rows.length >= ITEM_CEILING;
  if (!data.pending && !truncated) data.loadAll(ITEM_CEILING);

  const events = useMemo(
    () =>
      calendarEvents({
        items: rows.map((row) => ({ id: row.id, name: row.name, values: row.values })),
        dateColumnIds,
        rangeStart: grid.start,
        rangeEnd: grid.end,
      }),
    [rows, dateColumnIds, grid.start, grid.end],
  );
  const byDay = useMemo(() => eventsByDay(events), [events]);
  const rowsById = useMemo(() => new Map(rows.map((row) => [String(row.id), row])), [rows]);

  const bucketOf = useCallback(
    (itemId: string): CellBucket => {
      const item = rowsById.get(itemId);
      if (item === undefined || colorColumn === undefined) return EMPTY_BUCKET;
      const definition = findColumnType(colorColumn.type);
      const buckets = definition?.bucketsOf?.(item.values[colorColumn.id] ?? null, colorColumn.settings, {}) ?? [];
      return buckets[0] ?? EMPTY_BUCKET;
    },
    [rowsById, colorColumn],
  );

  const legend = useMemo(() => {
    const seen = new Map<string, CellBucket>();
    for (const event of events) {
      const bucket = bucketOf(event.itemId);
      seen.set(String(bucket.key), bucket);
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [events, bucketOf]);

  const move = useCallback(
    (event: CalendarEvent, targetDay: string) => {
      const item = rowsById.get(event.itemId);
      const column = columns.find((entry) => entry.id === event.columnId);
      if (item === undefined || column === undefined) return;
      if (data.payload?.permissions.readOnlyColumnIds.includes(column.id) === true) return;
      const delta = dayDelta(event.startDay, targetDay, event.day);
      if (delta === 0) return;
      const next = shiftCellValue(item.values[column.id] ?? null, delta);
      void source
        .setCellValues({
          boardId: boardId as never,
          writes: [{ itemId: event.itemId as never, columnId: column.id, value: next }],
        })
        .then(() => {
          setError(null);
          data.refresh();
        })
        .catch((reason: unknown) => {
          setError(reason instanceof Error ? reason.message : 'The item could not be moved');
          data.refresh();
        });
    },
    [rowsById, columns, data, source, boardId],
  );

  /* ── dnd-kit wiring (`shared/dnd.ts`) ─────────────────────────────────── */

  const sensors = useViewDragSensors();
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const draggingEvent = useMemo(
    () => (draggingKey === null ? null : (events.find((entry) => eventKey(entry) === draggingKey) ?? null)),
    [draggingKey, events],
  );

  const onDragStart = useCallback((event: DragStartEvent) => setDraggingKey(String(event.active.id)), []);
  const onDragCancel = useCallback(() => setDraggingKey(null), []);
  const onDragEnd = useCallback(
    (dragEvent: DragEndEvent) => {
      setDraggingKey(null);
      if (dragEvent.over === null) return;
      const dropped = events.find((entry) => eventKey(entry) === String(dragEvent.active.id));
      if (dropped) move(dropped, String(dragEvent.over.id));
    },
    [events, move],
  );

  if (!handle.ready || probe.pending || (data.pending && data.payload === null)) {
    return <PageSkeleton variant="board" />;
  }
  if (data.error ?? probe.error) {
    return <PageEmptyState title="This board could not be read" hint={(data.error ?? probe.error)!.message} />;
  }
  if (dateColumnIds.length === 0) {
    return (
      <div className="ez-calview" data-testid="calendar-view">
        <PageEmptyState
          title="Calendar needs a date or timeline column"
          hint="Add one to this board and its items will appear on the grid."
          data-testid="calendar-no-date-column"
        />
      </div>
    );
  }

  const perCell = eventsPerDayCell(range === 'month' ? 149 : 400);

  return (
    <div className="ez-calview" data-testid="calendar-view" data-range={range}>
      {/* P-025: monday keeps the whole board bar on the calendar too, over its own month nav. */}
      <BoardViewToolbar
        controls={toolbar.controls}
        boardId={boardId}
        viewId={viewId}
        registryId="calendar"
        source={source}
        payload={data.payload}
        onChanged={() => data.refresh()}
      />
      <header className="ez-calview__header" data-testid="calendar-header">
        <button type="button" data-testid="calendar-today" onClick={() => setAnchor(today)}>
          Today
        </button>
        <button
          type="button"
          className="ez-calview__nav"
          aria-label="Previous"
          data-testid="calendar-prev"
          onClick={() => setAnchor(shiftAnchor(anchor, range, -1))}
        >
          <Icon icon={icons.chevronLeft} size={16} ignoreFocusStyle />
        </button>
        <button
          type="button"
          className="ez-calview__nav"
          aria-label="Next"
          data-testid="calendar-next"
          onClick={() => setAnchor(shiftAnchor(anchor, range, 1))}
        >
          <Icon icon={icons.chevronRight} size={16} ignoreFocusStyle />
        </button>
        <h2 className="ez-calview__title" data-testid="calendar-title">
          {rangeTitle(grid, range, anchor)}
        </h2>
        <select
          className="ez-calview__range"
          aria-label="Range"
          value={range}
          data-testid="calendar-range"
          onChange={(event) => setRange(event.target.value as CalendarRange)}
        >
          <option value="month">Month</option>
          <option value="week">Week</option>
          <option value="day">Day</option>
        </select>
        <ViewToolbar
          testId="calendar-toolbar"
          title="Calendar"
          fields={[
            {
              kind: 'columns',
              id: 'columns',
              label: 'Date columns',
              columns: columns
                .filter((column) => column.type === 'date' || column.type === 'timeline')
                .map((column) => ({ id: column.id, title: column.title, type: column.type })),
              selection: settings.dateColumns,
              // P-146: `calendarDateColumnIds` places **every** placeable column when the
              // selection is empty, so the pane opens with every box ticked rather than none.
              emptyMeans: 'all' as const,
              onChange: (selection) => handle.save({ dateColumns: selection }),
            },
            {
              kind: 'select',
              id: 'color_by_id',
              label: 'Colour by',
              value: settings.colorColumnId ?? '',
              options: [
                { value: '', label: 'None' },
                ...columns
                  .filter((column) => findColumnType(column.type)?.bucketVocabulary !== undefined)
                  .map((column) => ({ value: column.id, label: column.title })),
              ],
              onChange: (value) => handle.save({ colorColumnId: value === '' ? null : value }),
            },
            {
              kind: 'toggle',
              id: 'display_legend',
              label: 'Legend',
              value: settings.displayLegend,
              onChange: (value) => handle.save({ displayLegend: value }),
            },
          ]}
        />
      </header>

      {truncated ? (
        <div className="ez-calview__notice" data-testid="calendar-truncated">
          {`Showing the first ${String(ITEM_CEILING)} items.`}
        </div>
      ) : null}
      {error === null ? null : (
        <div className="ez-calview__error" role="alert" data-testid="calendar-error">
          {error}
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <div
          className="ez-calview__grid"
          role="grid"
          data-testid="calendar-grid"
          style={{ '--ez-calview-columns': grid.columns } as React.CSSProperties}
        >
          {grid.columns === 7 ? (
            <div className="ez-calview__weekdays" role="row" data-testid="calendar-weekdays">
              {weekdayLabels(settings.weekStartsOn).map((label) => (
                <div className="ez-calview__weekday" role="columnheader" key={label}>
                  {label}
                </div>
              ))}
            </div>
          ) : null}

          <div className="ez-calview__days">
            {grid.days.map((cell) => {
              const dayEvents = byDay.get(cell.day) ?? [];
              const shown = dayEvents.slice(0, perCell);
              return (
                <CalendarDay
                  key={cell.day}
                  cell={cell}
                  events={shown}
                  hidden={dayEvents.length - shown.length}
                  draggingKey={draggingKey}
                  bucketOf={bucketOf}
                  onOpenItem={(itemId) => navigate(`/boards/${boardId}/views/${viewId}/pulses/${itemId}`)}
                  onShowMore={() => setOpenDay(cell.day)}
                />
              );
            })}
          </div>
        </div>

        {/* The pill itself follows the pointer, the way monday's does. */}
        <DragOverlay dropAnimation={null}>
          {draggingEvent === null ? null : (
            <span
              className="ez-calview__event"
              style={{
                backgroundColor: bucketOf(draggingEvent.itemId).color ?? BLANK_COLOR,
                height: CALENDAR_METRICS.eventHeight,
              }}
            >
              {draggingEvent.name}
            </span>
          )}
        </DragOverlay>
      </DndContext>

      {settings.displayLegend && legend.length > 0 ? (
        <div className="ez-calview__legend" data-testid="calendar-legend">
          {legend.map((bucket) => (
            <span className="ez-calview__legend-item" key={String(bucket.key)}>
              <i style={{ backgroundColor: bucket.color ?? BLANK_COLOR }} />
              {bucket.label}
            </span>
          ))}
        </div>
      ) : null}

      {openDay === null ? null : (
        <div className="ez-calview__day-popover" role="dialog" data-testid="calendar-day-popover">
          <header>
            <b>{openDay}</b>
            <button type="button" aria-label="Close" data-testid="calendar-day-popover-close" onClick={() => setOpenDay(null)}>
              <Icon icon={icons.close} size={16} ignoreFocusStyle />
            </button>
          </header>
          <ul>
            {(byDay.get(openDay) ?? []).map((event) => (
              <li key={eventKey(event)}>
                <button
                  type="button"
                  style={{ backgroundColor: bucketOf(event.itemId).color ?? BLANK_COLOR }}
                  onClick={() => navigate(`/boards/${boardId}/views/${viewId}/pulses/${event.itemId}`)}
                >
                  {event.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * One day cell: the droppable, its number, its pills and the `+N more` button.
 *
 * It is a component rather than an inline block because `useDroppable` is a hook and a hook
 * cannot be called inside a `.map`.
 */
function CalendarDay(props: {
  cell: { day: string; inRange: boolean; isToday: boolean };
  events: readonly CalendarEvent[];
  hidden: number;
  draggingKey: string | null;
  bucketOf(itemId: string): CellBucket;
  onOpenItem(itemId: string): void;
  onShowMore(): void;
}) {
  const { cell } = props;
  const { setNodeRef, isOver } = useDroppable({ id: cell.day });

  return (
    <div
      ref={setNodeRef}
      className="ez-calview__day"
      role="gridcell"
      data-testid={`calendar-day-${cell.day}`}
      data-off-range={!cell.inRange || undefined}
      data-today={cell.isToday || undefined}
      data-over={isOver || undefined}
      aria-label={cell.day}
    >
      <span className="ez-calview__day-number" data-testid={`calendar-day-number-${cell.day}`}>
        {cell.day.slice(8, 10)}
      </span>
      {props.events.map((event) => (
        <CalendarPill
          key={eventKey(event)}
          event={event}
          bucket={props.bucketOf(event.itemId)}
          dragging={props.draggingKey === eventKey(event)}
          onOpen={props.onOpenItem}
        />
      ))}
      {props.hidden > 0 ? (
        <button
          type="button"
          className="ez-calview__more"
          data-testid={`calendar-more-${cell.day}`}
          onClick={props.onShowMore}
        >
          {`+${String(props.hidden)} more`}
        </button>
      ) : null}
    </div>
  );
}

/** One event pill — a button that opens the item, and dnd-kit's draggable. */
function CalendarPill(props: {
  event: CalendarEvent;
  bucket: CellBucket;
  dragging: boolean;
  onOpen(itemId: string): void;
}) {
  const { event } = props;
  const { attributes, listeners, setNodeRef } = useDraggable({ id: eventKey(event) });
  // The node is already a `<button>`; `useDraggable`'s own `role="button"` is redundant.
  const { role: _role, ...aria } = attributes;

  return (
    <button
      {...aria}
      {...listeners}
      ref={setNodeRef}
      type="button"
      className="ez-calview__event"
      data-testid={`calendar-event-${event.itemId}-${event.columnId}-${event.day}`}
      style={{
        backgroundColor: props.bucket.color ?? BLANK_COLOR,
        height: CALENDAR_METRICS.eventHeight,
        opacity: props.dragging ? 0.4 : undefined,
      }}
      title={event.time === null ? event.name : `${event.name} · ${event.time}`}
      onClick={() => props.onOpen(event.itemId)}
    >
      {event.name}
    </button>
  );
}

function eventKey(event: CalendarEvent): string {
  return `${event.itemId}:${event.columnId}:${event.day}`;
}

/** The whole-day shift a drop means: where the value starts vs. where this pill landed. */
function dayDelta(startDay: string, targetDay: string, draggedDay: string): number {
  const from = Date.parse(`${draggedDay}T00:00:00Z`);
  const to = Date.parse(`${targetDay}T00:00:00Z`);
  void startDay;
  return Math.round((to - from) / 86_400_000);
}

function resolveColorColumn(
  columns: readonly BoardColumn[],
  colorColumnId: string | null,
): BoardColumn | undefined {
  if (colorColumnId !== null) return columns.find((column) => column.id === colorColumnId);
  return columns.find((column) => findColumnType(column.type)?.bucketVocabulary !== undefined);
}

export type { ItemRow };
